import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bell, Send, Smartphone, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function NotifyCenter({ theme, onClose }) {
  const light = theme === 'light';
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState('');
  const [pushState, setPushState] = useState('unknown');

  const load = useCallback(() => {
    axios.get(`${API}/admin/notify/config`, { withCredentials: true }).then(r => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushState('unsupported'); return; }
    navigator.serviceWorker.getRegistration('/sw-admin.js').then(async (reg) => {
      const sub = reg && await reg.pushManager.getSubscription();
      setPushState(sub ? 'on' : 'off');
    }).catch(() => setPushState('off'));
  }, []);

  const enablePush = async () => {
    setBusy('push');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Autorisation refusée dans les réglages du navigateur'); return; }
      const reg = await navigator.serviceWorker.register('/sw-admin.js');
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-key`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.key) });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, { withCredentials: true });
      setPushState('on'); load();
      toast.success('Notifications push activées sur cet appareil !');
    } catch (e) {
      toast.error("Échec d'activation — sur iPhone, installe d'abord l'app sur l'écran d'accueil (Partager → Sur l'écran d'accueil)");
    } finally { setBusy(''); }
  };

  const saveToken = async () => {
    setBusy('token');
    try {
      const { data } = await axios.post(`${API}/admin/notify/telegram/token`, { token }, { withCredentials: true });
      toast.success(`Bot @${data.bot_name} connecté !`);
      setToken(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Token invalide'); }
    finally { setBusy(''); }
  };

  const detectChat = async () => {
    setBusy('chat');
    try {
      const { data } = await axios.post(`${API}/admin/notify/telegram/detect-chat`, {}, { withCredentials: true });
      toast.success(`Chat lié : ${data.chat_name || data.chat_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(''); }
  };

  const sendTest = async () => {
    setBusy('test');
    try { await axios.post(`${API}/admin/notify/test`, {}, { withCredentials: true }); toast.success('Notification test envoyée !'); }
    catch { toast.error('Erreur'); }
    finally { setBusy(''); }
  };

  const box = light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10';
  const tx = light ? 'text-gray-900' : 'text-white';
  const th = light ? 'text-gray-500' : 'text-white/40';
  const inp = light ? 'bg-white border-gray-300 text-gray-900' : 'bg-[#11041F] border-white/15 text-white';

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-4" data-testid="notify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className={`relative w-full sm:w-[430px] max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5 ${light ? 'bg-white' : 'bg-[#1a0e2e] border border-white/10'}`}>
        <div className="flex items-start justify-between mb-4">
          <h3 className={`text-sm font-bold ${tx}`}><Bell size={14} className="inline mr-1.5 text-[#E9641A]" />Notifications mobiles</h3>
          <button onClick={onClose} className={`p-1 rounded-lg ${light ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-white/10 text-white/60'}`} data-testid="notify-center-close"><X size={15} /></button>
        </div>

        {/* Push PWA */}
        <div className={`rounded-xl border p-4 mb-3 ${box}`}>
          <p className={`text-xs font-bold mb-1 ${tx}`}><Smartphone size={12} className="inline mr-1" />Notifications sur cet appareil (PWA)</p>
          <p className={`text-[11px] leading-relaxed mb-3 ${th}`}>
            Sur iPhone : ouvre <b>mayaplus.fr/admin</b> dans Safari → bouton Partager → <b>« Sur l'écran d'accueil »</b>. Ouvre ensuite l'app <b>Maya+ Admin</b> installée, reviens ici et active.
          </p>
          {pushState === 'on' ? (
            <p className="text-xs font-semibold text-emerald-500" data-testid="push-status-on"><Check size={12} className="inline mr-1" />Activées sur cet appareil</p>
          ) : (
            <button onClick={enablePush} disabled={busy === 'push' || pushState === 'unsupported'} data-testid="enable-push-btn"
              className="w-full py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-bold transition-colors disabled:opacity-50">
              {pushState === 'unsupported' ? 'Non supporté ici — installe la PWA d\'abord' : busy === 'push' ? 'Activation…' : 'Activer les notifications push'}
            </button>
          )}
        </div>

        {/* Telegram */}
        <div className={`rounded-xl border p-4 mb-3 ${box}`}>
          <p className={`text-xs font-bold mb-1 ${tx}`}><Send size={12} className="inline mr-1" />Telegram</p>
          {cfg?.telegram_token_set && cfg?.telegram_chat_id ? (
            <p className="text-xs font-semibold text-emerald-500" data-testid="telegram-status-on">
              <Check size={12} className="inline mr-1" />Bot @{cfg.telegram_bot_name} connecté — alertes actives
            </p>
          ) : (
            <>
              <ol className={`text-[11px] leading-relaxed mb-3 list-decimal ml-4 ${th}`}>
                <li>Sur Telegram, cherche <b>@BotFather</b> → envoie <b>/newbot</b> → suis les étapes</li>
                <li>Copie le <b>token</b> reçu et colle-le ci-dessous</li>
                <li>Envoie n'importe quel message à ton nouveau bot</li>
                <li>Clique « Lier mon compte »</li>
              </ol>
              {!cfg?.telegram_token_set ? (
                <div className="flex gap-2">
                  <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="123456:ABC-DEF…" data-testid="telegram-token-input"
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs ${inp}`} />
                  <button onClick={saveToken} disabled={!token || busy === 'token'} data-testid="telegram-token-save"
                    className="px-3 py-2 rounded-lg bg-[#E9641A] text-white text-xs font-bold disabled:opacity-50">OK</button>
                </div>
              ) : (
                <button onClick={detectChat} disabled={busy === 'chat'} data-testid="telegram-detect-chat"
                  className="w-full py-2.5 rounded-lg bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-bold transition-colors disabled:opacity-50">
                  {busy === 'chat' ? 'Recherche…' : `Lier mon compte (bot @${cfg?.telegram_bot_name})`}
                </button>
              )}
            </>
          )}
        </div>

        <button onClick={sendTest} disabled={busy === 'test'} data-testid="notify-test-btn"
          className={`w-full py-2.5 rounded-lg border text-xs font-bold transition-colors ${light ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-white/15 text-white/70 hover:bg-white/5'}`}>
          {busy === 'test' ? 'Envoi…' : 'Envoyer une notification test 🔔'}
        </button>

        <p className={`text-[10px] mt-3 ${th}`}>
          Alertes envoyées : nouvel abonné, résiliation, échec de paiement, inscription, nouvel avis, signalement, alerte piratage, email support, record d'audience.
        </p>
      </div>
    </div>
  );
}
